/**
 * Pure formatting utilities used by the document generator.
 *
 * Contains only side-effect free helpers for action/operator/axis descriptions.
 */
import type { Action } from 'flow/store/engine/types';
import { OPERATORS } from '../../../editor/components/thresholds/operators/constants';

/** Returns a user-facing imperative description for an action. */
export function getActionDescription(action: Action | null): string {
  if (!action) return '';

  switch (action.type) {
    case 'service':
      return `Run service "${action.service?.name}"`;
    case 'tag':
      return `Tag "${action.tag?.name}"`;
    case 'input':
      return `Input "${action.input?.name}"`;
    case 'output':
      return `Output "${action.output?.name}"`;
    case 'exit':
      return 'Exit workflow';
    case 'outcome':
      return `Set outcome "${action.outcome?.name}"`;
    case 'outcome_reason':
      return `Set outcome reason "${action.outcomeReason?.name}"`;
    case 'matrix_model':
      return `Run matrix model "${action.matrixModel?.name}"`;
    case 'imported_matrix_model':
      return `Run imported matrix model "${action.importedMatrixModel?.name}"`;
    case 'imported_output_attribute':
      return `Use imported output attribute "${action.importedOutputAttribute?.name}"`;
    case 'workflow':
      return `Run workflow "${action.workflow?.name}"`;
    case 'preview':
      return 'Preview action';
    default:
      return `Unknown action type: ${action.type}`;
  }
}

/** Returns a noun-phrase description used in threshold sentences. */
export function getActionDescriptionForThreshold(action: Action | null): string {
  if (!action) return '';

  switch (action.type) {
    case 'service':
      return `"${action.service?.name}" service`;
    case 'tag':
      return `"${action.tag?.name}" tag`;
    case 'input':
      return `"${action.input?.name}" input`;
    case 'output':
      return `"${action.output?.name}" output`;
    case 'exit':
      return 'exit workflow';
    case 'outcome':
      return `"${action.outcome?.name}" outcome`;
    case 'outcome_reason':
      return `"${action.outcomeReason?.name}" outcome reason`;
    case 'matrix_model':
      return `"${action.matrixModel?.name}" matrix model`;
    case 'imported_matrix_model':
      return `"${action.importedMatrixModel?.name}" imported matrix model`;
    case 'imported_output_attribute':
      return `"${action.importedOutputAttribute?.name}" imported output attribute`;
    case 'workflow':
      return `"${action.workflow?.name}" workflow`;
    case 'preview':
      return 'preview action';
    default:
      return `unknown action type: ${action.type}`;
  }
}

/** Maps an operator key to its display label. */
export function getOperatorDescription(operator: string): string {
  const operatorConfig = OPERATORS[operator as keyof typeof OPERATORS];
  return operatorConfig?.text || operator;
}

/** Formats axis header values, handling infinities and decimals. */
export function formatAxisValue(value: string | number): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value >= Number.MAX_SAFE_INTEGER || value === Number.MAX_VALUE) {
    return '∞';
  }

  if (value <= -Number.MAX_SAFE_INTEGER || value === -Number.MAX_VALUE) {
    return '-∞';
  }

  if (Number.isInteger(value)) {
    return value.toString();
  }

  return value.toFixed(2).replace(/\.?0+$/, '');
}

/** Pretty-prints an axis header definition. */
export function describeMatrixAxis(axis: {
  order: string | null;
  axisHeaderDataType: string;
  axisHeader: (string | number)[];
}): string {
  const formattedValues = axis.axisHeader
    .map(value => formatAxisValue(value))
    .join(', ');

  return `${axis.axisHeaderDataType} values ${
    axis.order ? `(${axis.order} order)` : ''
  }: ${formattedValues}`;
}

